import { Component, type ReactNode } from 'react';
import { devError } from '../../utils/dev-log';
import { isFirestoreAssertionError } from '../../utils/firestore-errors';

interface PageErrorBoundaryProps {
  children: ReactNode;
  pageName: string;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface PageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  state: PageErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isFirestoreAssertionError(error)) {
      devError(`[PageErrorBoundary:${this.props.pageName}] Firestore watch stream error — reload required`, error.message);
      return;
    }
    devError(`[PageErrorBoundary:${this.props.pageName}]`, error, info.componentStack);

    // Report to backend (fire-and-forget)
    const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003';
    fetch(`${apiUrl}/v1/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'admin',
        message: error.message,
        stack: error.stack,
        context: {
          page: this.props.pageName,
          url: window.location.href,
          browser: navigator.userAgent,
          componentStack: info.componentStack,
        },
      }),
    }).catch((err) => devError('Error report dispatch failed', err));
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error!, this.handleReset);
      }

      const isTransient = isFirestoreAssertionError(this.state.error);

      return (
        <div className="flex items-center justify-center py-16 px-6">
          <div className="max-w-md w-full bg-white border border-surface-200 rounded-lg shadow-sm p-8 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-surface-900 mb-1">
              {isTransient ? 'Connection interrupted' : 'Something went wrong'}
            </h2>
            <p className="text-sm text-surface-500 mb-1">
              {isTransient
                ? 'The real-time connection was interrupted. This can happen when switching between pages quickly.'
                : <>An error occurred in <span className="font-medium">{this.props.pageName}</span>.</>}
            </p>
            {isTransient
              ? <div className="mb-6" />
              : <p className="text-xs text-surface-400 mb-6">{this.state.error?.message || 'An unexpected error occurred.'}</p>
            }
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
